import { clearServiceWorkerCaches } from '../serviceWorker';
import { gaeApp } from '../components/helpers/Analytics';

/** One forced reload per tab within this window; a second miss means it did not help. */
const RELOAD_GUARD_KEY = 'pwa-stale-build-reload';
const RELOAD_GUARD_TTL_MS = 10 * 60 * 1000;

// Empty module while reload is pending: `null` makes the router throw on `.default`.
const EMPTY_MODULE = { default: () => null };

let dialogs = null;
let beforeReload = null;

/**
 * @param {{
 *   staleBuild: function({saved: boolean, lost: boolean}, function): void,
 *   staleBuildFailed: function(): void,
 *   networkError: function(function): void
 * }} _dialogs staleBuild(saveResult, doReload), networkError(doRetry)
 * @param {function(): Promise<{saved: boolean, lost: boolean}>} _beforeReload
 *   keeps user data before the forced reload
 */
export const initImportWithRetry = (_dialogs, _beforeReload) => {
  dialogs = _dialogs;
  beforeReload = _beforeReload;
};

const isReloadGuardActive = () => {
  try {
    const at = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY));
    return Boolean(at) && Date.now() - at < RELOAD_GUARD_TTL_MS;
  } catch (e) {
    return false;
  }
};

const setReloadGuard = () => {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch (e) {
    // private mode: no guard, worst case one more reload
  }
};

/**
 * 'stale' — the chunk is gone (build of a previous release, server answers 404
 * or HTML); 'network' — the chunk exists or the device is offline, retry helps.
 */
export const classifyChunkError = async error => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'network';
  }
  const url = error && error.request;
  if (!url) {
    return /Unexpected token|expected expression/.test(
      String(error && error.message)
    )
      ? 'stale'
      : 'network';
  }
  try {
    const response = await fetch(url, { cache: 'no-store' });
    const contentType = response.headers.get('content-type') || '';
    return !response.ok || contentType.indexOf('text/html') !== -1
      ? 'stale'
      : 'network';
  } catch (e) {
    return 'network';
  }
};

const reloadApp = async () => {
  window.localStorage.setItem('page-has-been-force-refreshed', 'true');
  // Browser tabs share SW caches with an installed app on the same origin.
  if (window.standalone) {
    try {
      await clearServiceWorkerCaches();
    } catch (e) {
      // ignore cache cleanup failures
    }
  }
  window.location.reload();
};

const onStaleBuild = async () => {
  if (isReloadGuardActive()) {
    // ERROR_ON_* → ERROR span: the user is stuck, must show up in error=true.
    gaeApp('ERROR_ON_STALE_BUILD_RELOAD');
    if (dialogs) dialogs.staleBuildFailed();
    return;
  }
  setReloadGuard();

  let result = { saved: false, lost: false };
  if (beforeReload) {
    try {
      result = await beforeReload();
    } catch (e) {
      result = { saved: false, lost: true };
    }
  }
  gaeApp(
    'STALE_BUILD_FORCED_RELOAD',
    result.saved ? 'saved' : result.lost ? 'lost' : 'nothing'
  );

  if (dialogs) {
    dialogs.staleBuild(result, reloadApp);
  } else {
    reloadApp();
  }
};

const loadChunk = async (componentImport, attempt) => {
  try {
    const component = await componentImport();
    window.localStorage.setItem('page-has-been-force-refreshed', 'false');
    return component;
  } catch (error) {
    const kind = await classifyChunkError(error);
    gaeApp(
      'CHUNK_LOAD_FAILED',
      JSON.stringify({
        kind,
        type: error && error.type,
        chunk: error && error.request,
        attempt
      })
    );

    if (kind === 'network' && dialogs) {
      return new Promise(resolve =>
        dialogs.networkError(() => {
          gaeApp('CHUNK_LOAD_RETRY', String(attempt + 1));
          resolve(loadChunk(componentImport, attempt + 1));
        })
      );
    }

    if (kind === 'stale') {
      await onStaleBuild();
    }
    return EMPTY_MODULE;
  }
};

export const importWithRetry = componentImport => () =>
  loadChunk(componentImport, 1);
